import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, COMPANY_KEY, errorMessage } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [profile, setProfile] = useState(undefined);
  const [blocked, setBlocked] = useState(null);

  const refreshProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setProfile(data);
      setBlocked(null);
      return data;
    } catch (e) {
      if (e.response?.status === 403) setBlocked(errorMessage(e));
      setProfile(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshProfile();
    const onExpired = () => setProfile(null);
    window.addEventListener("auth:expired", onExpired);
    return () => {
      window.removeEventListener("auth:expired", onExpired);
    };
  }, [refreshProfile]);

  const signIn = useCallback(async (email, password) => {
    await api.post("/auth/login", { email, password });
    const { data: me } = await api.get("/auth/me");
    setBlocked(null);
    setProfile(me);
    return me;
  }, []);

  const signUp = useCallback(async (body) => {
    await api.post("/auth/signup", body);
    const { data: me } = await api.get("/auth/me");
    setBlocked(null);
    setProfile(me);
    return me;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem(COMPANY_KEY);
      setProfile(null);
      setBlocked(null);
    }
  }, []);

  const value = useMemo(
    () => ({ profile, setProfile, blocked, loading: profile === undefined, signIn, signUp, signOut, refreshProfile }),
    [profile, blocked, signIn, signUp, signOut, refreshProfile],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
